import { useEffect } from "react";
import { ALLIANCE_DONATIONS, ALLIANCE_DONATION_DAILY_CAP, allianceCan, type DonationLot } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { useBalances } from "../state/balances.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { Icon } from "../ui/Icon.js";
import { ResourceIcon } from "../assets/gameIcons.js";
import { formatCompact } from "../ui/Counter.js";
import { useAlliance } from "./state.js";
import type { AllianceView } from "./types.js";

const GROUPS: DonationLot["currency"][] = ["RAT", "POL", "ALY", "ENC", "credits"];

/** Contribute tab (D72): donation lots the wallet covers, today's count against the cap, and the focused building's progress. */
export function Contribute({ alliance }: { alliance: AllianceView }) {
  const t = useT();
  const balances = useBalances((s) => s.balances);
  const buildings = useAlliance((s) => s.buildings);
  const donate = useAlliance((s) => s.donate);
  const refreshBuildings = useAlliance((s) => s.refreshBuildings);
  useEffect(() => {
    if (!buildings) void refreshBuildings();
  }, [buildings, refreshBuildings]);
  const capped = alliance.donationsToday >= ALLIANCE_DONATION_DAILY_CAP;
  const have = (lot: DonationLot) => (lot.currency === "credits" ? balances?.credits ?? 0 : balances?.[lot.currency] ?? 0);
  const focus = buildings?.find((b) => b.id === alliance.focusBuilding) ?? null;
  const officer = alliance.myRole !== null && allianceCan(alliance.myRole, "focus");
  return (
    <div className="alliance-contribute" data-testid="alliance-contribute">
      <Panel title={t("alliance.contribute.title")}>
        <p className="hint">{t("alliance.contribute.hint")}</p>
        <p className="mono" data-testid="donations-today">{t("alliance.contribute.today", { n: alliance.donationsToday, max: ALLIANCE_DONATION_DAILY_CAP })}{capped ? ` · ${t("alliance.contribute.capped")}` : ""}</p>
        {GROUPS.map((currency) => (
          <div key={currency} className="alliance-lots" data-testid={`lots-${currency}`}>
            <span className="alliance-lot-label">{currency === "credits" ? <><Icon name="shop" /> {t("store.credits")}</> : <><ResourceIcon resource={currency} size={18} label={t(`res.${currency}`)} /> {t(`res.${currency}`)}</>}</span>
            {ALLIANCE_DONATIONS.filter((l) => l.currency === currency).map((lot) => (
              <Button key={lot.id} disabled={capped || have(lot) < lot.amount} onClick={() => void donate(lot.id)} title={t("alliance.contribute.lotTitle", { xp: lot.xp, coins: lot.coins, progress: lot.progress })} data-testid={`donate-${lot.id}`}>
                {formatCompact(lot.amount)}
                <small>+{lot.xp} XP · +{lot.coins} <Icon name="bookmark" /></small>
              </Button>
            ))}
          </div>
        ))}
      </Panel>
      <Panel title={t("alliance.contribute.focus")}>
        {focus ? (
          <div className="alliance-focus" data-testid="focus-card">
            <strong>{pick(focus.name)}</strong>
            <span className="hint">{t("alliance.buildings.level", { n: focus.level, max: focus.max })} · {t(`alliance.effect.${focus.effect.key}`, { n: focus.effect.perLevel })}</span>
            {focus.next !== null ? (
              <>
                <div className="alliance-progress"><span style={{ width: `${Math.min(100, Math.round((focus.progress / focus.next) * 100))}%` }} /></div>
                <span className="mono hint">{focus.progress}/{focus.next}</span>
              </>
            ) : (
              <span className="hint">{t("alliance.buildings.capped")}</span>
            )}
          </div>
        ) : (
          <p className="hint" data-testid="focus-none">{officer ? t("alliance.contribute.noFocusOfficer") : t("alliance.contribute.noFocus")}</p>
        )}
        <div className="row" style={{ marginTop: 8 }}>
          <Button onClick={() => navigate("/app/alliance/buildings")} data-testid="focus-open-buildings"><Icon name="build" /> {t("alliance.tab.buildings")}</Button>
        </div>
      </Panel>
    </div>
  );
}
